/**
 * SpotlightOverlay (session 126p)
 *
 * Generic spotlight primitive used by the onboarding tour and (later)
 * any "what's new" feature highlight. Renders:
 *
 *   • A semi-opaque backdrop covering the entire viewport.
 *   • A transparent cutout sized + positioned over a target DOM
 *     element (located via a CSS selector string).
 *   • A tooltip card placed adjacent to the cutout with title, body,
 *     and navigation buttons (Back / Next / Skip).
 *
 * Recomputes the cutout rect on every resize + scroll so the
 * spotlight stays glued to the moving target on small screens.
 *
 * Usage:
 *   <SpotlightOverlay
 *     target='[data-testid="nav-home"]'
 *     title="Home"
 *     body="Your dashboard for everything PeopleiKnow."
 *     stepNumber={1}
 *     totalSteps={5}
 *     onNext={...}
 *     onSkip={...}
 *   />
 *
 * Pure rendering — state lives in the parent (tour state machine).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';

const PADDING = 8;
const TOOLTIP_GAP = 12;
const TOOLTIP_WIDTH = 320;

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    x: Math.max(0, r.left - PADDING),
    y: Math.max(0, r.top - PADDING),
    w: r.width + PADDING * 2,
    h: r.height + PADDING * 2,
  };
}

function computeTooltipPlacement(rect) {
  if (!rect) return { left: 16, top: 16 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer below the cutout; fall back to above if below is off-screen.
  const below = rect.y + rect.h + TOOLTIP_GAP;
  const above = rect.y - TOOLTIP_GAP;
  let top;
  if (below + 200 <= vh) top = below;
  else if (above >= 200) top = above - 200;
  else top = Math.max(16, (vh - 200) / 2);
  // Center horizontally on cutout, clamped to viewport.
  let left = rect.x + rect.w / 2 - TOOLTIP_WIDTH / 2;
  left = Math.max(12, Math.min(vw - TOOLTIP_WIDTH - 12, left));
  return { left, top };
}

export default function SpotlightOverlay({
  target,
  title,
  body,
  stepNumber,
  totalSteps,
  canBack,
  onBack,
  onNext,
  onSkip,
  nextLabel = 'Next',
  finalStep = false,
}) {
  const [rect, setRect] = useState(() => getRect(target));
  const rafRef = useRef(null);

  useEffect(() => {
    let frames = 0;
    const recompute = () => {
      setRect(getRect(target));
    };
    // Retry up to ~1s in case the target element mounts after the
    // user's navigation finishes (route transitions are async).
    const interval = setInterval(() => {
      const r = getRect(target);
      setRect(r);
      frames += 1;
      if (r || frames >= 20) clearInterval(interval);
    }, 50);
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  const tooltip = computeTooltipPlacement(rect);

  return (
    <div className="fixed inset-0 z-[1000] pointer-events-none" data-testid="spotlight-overlay">
      {/* Backdrop with cutout drawn via four divs (so we keep the
          cutout area free of click-blocking). When the target hasn't
          mounted yet we still show the dark overlay so the user knows
          something's happening. */}
      {rect ? (
        <>
          {/* Top */}
          <div className="absolute inset-x-0 bg-black/55 pointer-events-auto" style={{ top: 0, height: rect.y }} />
          {/* Bottom */}
          <div className="absolute inset-x-0 bg-black/55 pointer-events-auto" style={{ top: rect.y + rect.h, bottom: 0 }} />
          {/* Left */}
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: rect.y, height: rect.h, left: 0, width: rect.x }} />
          {/* Right */}
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: rect.y, height: rect.h, left: rect.x + rect.w, right: 0 }} />
          {/* Cutout ring */}
          <div
            className="absolute rounded-xl pointer-events-none ring-4 ring-nav-blue shadow-[0_0_0_4px_rgba(255,255,255,0.4)] animate-pulse"
            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/55 pointer-events-auto" />
      )}

      {/* Tooltip */}
      <div
        className="absolute bg-card border border-border rounded-xl shadow-2xl p-4 pointer-events-auto"
        style={{ left: tooltip.left, top: tooltip.top, width: TOOLTIP_WIDTH }}
        data-testid="spotlight-tooltip"
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Tour &middot; {stepNumber} of {totalSteps}
          </span>
          <button
            type="button"
            onClick={onSkip}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Skip tour"
            data-testid="spotlight-skip"
          >
            <X size={14} />
          </button>
        </div>
        <h3 className="font-heading text-base font-semibold mb-1.5" data-testid="spotlight-title">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3" data-testid="spotlight-body">{body}</p>
        <div className="flex items-center gap-2">
          {canBack && (
            <Button
              size="sm"
              variant="outline"
              onClick={onBack}
              className="rounded-full h-8 text-xs"
              data-testid="spotlight-back"
            >
              <ArrowLeft size={12} className="mr-1" /> Back
            </Button>
          )}
          <div className="flex-1" />
          <Button
            size="sm"
            onClick={onNext}
            className="rounded-full h-8 text-xs bg-nav-blue hover:bg-nav-blue/80"
            data-testid="spotlight-next"
          >
            {finalStep ? 'Finish' : nextLabel}
            {!finalStep && <ArrowRight size={12} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
